import { Button } from "@mui/material";
import { UiAlert } from "../dialog/alert";
import { styled } from "@mui/material/styles";
import "./button.scss";
import { Edit, Eye, Ban, FolderCheck } from "lucide-react";
import { IconButton, Tooltip } from "@radix-ui/themes";

const BootstrapButton = styled(Button)({
  boxShadow: "none",
  textTransform: "none",
  fontSize: "14px",
  borderRadius: "8px",
  lineHeight: "100%",
  minWidth: "auto",
  fontFamily: [
    "-apple-system",
    "BlinkMacSystemFont",
    '"Segoe UI"',
    "Roboto",
    '"Helvetica Neue"',
    "Arial",
    "sans-serif",
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
  ].join(","),
});

interface UiButtonProps {
  variant?: "contained" | "outlined" | "text";
  className?: string;
  color?:
    | "error"
    | "primary"
    | "secondary"
    | "inherit"
    | "success"
    | "info"
    | "warning";
  loading?: boolean;
  children: React.ReactNode;
  startIcon?: React.ReactNode;
  onClick?: () => void;
  onCancel?: () => void;
  size?: "small" | "medium" | "large";
  withAlert?: boolean;
  confirmTitle?: string;
  confirmMessage?: string;
  okLabel?: string;
  cancelLabel?: string;
  disabled?: boolean;
  styles?: React.CSSProperties;
  type?: "button" | "submit" | "reset" | undefined;
}

export function UiButton({
  variant = "contained",
  children,
  loading,
  className,
  startIcon,
  color,
  onClick,
  size,
  withAlert,
  confirmTitle = "Alerte confirmation",
  confirmMessage = "Voulez-vous vraiment continuer ?",
  okLabel = "Valider",
  cancelLabel = "Annuler",
  disabled,
  styles,
  type,
  onCancel,
}: UiButtonProps) {
  const styless = styles ?? {};
  return (
    <>
      {withAlert && (
        <UiAlert
          title={confirmTitle}
          message={confirmMessage}
          onCancel={onCancel}
          onOk={onClick}
          okLabel={okLabel}
          cancelLabel={cancelLabel}
        >
          <BootstrapButton
            variant={variant}
            color={color ?? "primary"}
            loading={loading}
            id="ui-button"
            className={`ui-button normal-case ${className}`}
            startIcon={startIcon}
            disabled={disabled}
            type={type}
            size={size}
            style={{
              height: size === "small" ? "32px" : "44px",
            }}
          >
            {children}
          </BootstrapButton>
        </UiAlert>
      )}
      {!withAlert && (
        <BootstrapButton
          onClick={onClick}
          variant={variant}
          color={color ?? "primary"}
          loading={loading}
          disabled={disabled}
          className={`ui-button normal-case ${className}`}
          startIcon={startIcon}
          type={type}
          size={size}
          style={{
            height: size === "small" ? "32px" : "44px",
            borderRadius: size === "small" ? "4px" : "8px",
            ...styless,
          }}
        >
          {children}
        </BootstrapButton>
      )}
    </>
  );
}

interface UiCustomButtonProps {
  onClick?: () => void;
  title?: string;
  withAlert?: boolean;
  confirmTitle?: string;
  confirmMessage?: string;
  okLabel?: string;
  cancelLabel?: string;
  label?: string;
  loading?: boolean;
  onCancel?: () => void;
}

export const UiDetailButton = ({ onClick, title }: UiCustomButtonProps) => {
  return (
    <UiButton onClick={onClick} size="small" color="primary" className="px-2">
      <Tooltip content={title ?? "Voir détail"}>
        <Eye />
      </Tooltip>
    </UiButton>
  );
};

export const UiEditButton = ({ onClick, title }: UiCustomButtonProps) => {
  return (
    <UiButton onClick={onClick} size="small" color="info" className="px-2">
      <Tooltip content={title ?? "Modifier"}>
        <Edit />
      </Tooltip>
    </UiButton>
  );
};

export const UiSuspendButton = ({
  onClick,
  title,
  confirmTitle,
  confirmMessage,
  okLabel,
  cancelLabel,
  loading,
  label,
  onCancel,
}: UiCustomButtonProps) => {
  return (
    <UiButton
      size="small"
      className="px-2"
      onClick={onClick}
      color="error"
      withAlert={true}
      loading={loading}
      onCancel={onCancel}
      confirmTitle={confirmTitle}
      confirmMessage={
        confirmMessage ?? "Voulez-vous vraiment suspendre cette campagne ?"
      }
      okLabel={okLabel || "Suspendre"}
      cancelLabel={cancelLabel}
    >
      <Tooltip content={title ?? "Suspendre"}>
        <div className="flex w-auto gap-2 items-center">
          <Ban />
          {label !== undefined && <span>{label || ""}</span>}
        </div>
      </Tooltip>
    </UiButton>
  );
};

export const UiValidateCommandeButton = ({
  onClick,
  title,
  confirmTitle,
  confirmMessage,
  okLabel,
  cancelLabel,
  loading,
  label,
  onCancel,
}: UiCustomButtonProps) => {
  return (
    <UiButton
      size="small"
      className="px-2"
      onClick={onClick}
      color="success"
      withAlert={true}
      loading={loading}
      onCancel={onCancel}
      confirmTitle={confirmTitle}
      confirmMessage={
        confirmMessage ?? "Voulez-vous vraiment valider cette commande ?"
      }
      okLabel={okLabel}
      cancelLabel={cancelLabel}
    >
      <Tooltip content={title ?? "Valider la commande"}>
        <div className="flex w-auto gap-2 items-center">
          <FolderCheck />
          {label !== undefined && <span>{label || ""}</span>}
        </div>
      </Tooltip>
    </UiButton>
  );
};
